// src/store/realtimeSubscriptions.js
// ────────────────────────────────────────────────────────────
// Centralized Firestore subscription manager.
// Creates one realtime listener per collection, updates Zustand
// directly, and cleans up all listeners on logout.
//
// Usage:
//   Auth login  → initializeRealtimeSubscriptions(uid)
//   Auth logout → teardownRealtimeSubscriptions()
// ────────────────────────────────────────────────────────────
import { onLeadsChange } from '../firebase/leads.service';
import { onConversationsChange } from '../firebase/conversation.service';
import { onAutomationsChange } from '../firebase/automations.service';
import { onPropertiesChange } from '../firebase/property.service';
import { onFormResponsesChange } from '../firebase/formResponse.service';
import { subscribeToActiveExecutions } from '../firebase/automationRuns.service';
import { ensureFormFieldsHaveNames } from '../utils/formNameGenerator';
import useAppStore from './appStore';

/** Active unsubscribe functions keyed by listener name. */
let _unsubscribers = {};

/** Prevents double-initialization. */
let _initialized = false;

/**
 * Start all global Firestore listeners for the authenticated user.
 * Each listener writes directly to the Zustand store.
 * Call this once after successful authentication.
 *
 * @param {string} uid - The authenticated user's UID.
 */
export function initializeRealtimeSubscriptions(uid) {
  if (!uid) {
    console.warn('[realtimeSubscriptions] No uid provided — skipping initialization');
    return;
  }

  if (_initialized) {
    console.info('[realtimeSubscriptions] Already initialized — tearing down first');
    teardownRealtimeSubscriptions();
  }

  const store = useAppStore.getState();

  // Mark all collections as loading
  store.setLoading('leads', true);
  store.setLoading('conversations', true);
  store.setLoading('properties', true);
  store.setLoading('automations', true);
  store.setLoading('formResponses', true);

  // ── Leads ─────────────────────────────────────────────
  _unsubscribers.leads = onLeadsChange(uid, (leads) => {
    useAppStore.getState().setLeads(leads);
    useAppStore.getState().setLoading('leads', false);
  });

  // ── Conversations (also derives lastContactMap) ───────
  _unsubscribers.conversations = onConversationsChange(uid, (conversations) => {
    useAppStore.getState().setConversations(conversations);
    useAppStore.getState().setLoading('conversations', false);

    // Derive lastContactMap from conversations
    const map = {};
    conversations.forEach((convo) => {
      if (convo.leadId) {
        map[convo.leadId] = {
          lastMessageAt: convo.lastMessageAt || convo.lastUpdated,
          hasMessages: !!(convo.lastMessage && convo.lastMessage.trim()),
        };
      }
    });
    useAppStore.getState().setLastContactMap(map);
  });

  // ── Properties (with formName backward compat) ────────
  _unsubscribers.properties = onPropertiesChange(uid, (properties) => {
    const normalized = properties.map((prop) => {
      if (!prop.captureFormFields?.[0]?.formName) {
        return {
          ...prop,
          captureFormFields: ensureFormFieldsHaveNames(
            prop.captureFormFields,
            prop.address,
          ),
        };
      }
      return prop;
    });
    useAppStore.getState().setCtProperties(normalized);
    useAppStore.getState().setLoading('properties', false);
  });

  // ── Automations ──────────────────────────────────────
  _unsubscribers.automations = onAutomationsChange((automations) => {
    useAppStore.getState().setAutomations(automations);
    useAppStore.getState().setLoading('automations', false);
  });

  // ── Form Responses ───────────────────────────────────
  _unsubscribers.formResponses = onFormResponsesChange(uid, (responses) => {
    useAppStore.getState().setCtFormResponses(responses);
    useAppStore.getState().setLoading('formResponses', false);
  });

  // ── Active Automation Executions (global, for lead badges) ──
  _unsubscribers.executionMap = subscribeToActiveExecutions((grouped) => {
    useAppStore.getState().setExecutionMap(grouped);
  });

  _initialized = true;
  console.info('[realtimeSubscriptions] All listeners initialized for uid:', uid);
}

/**
 * Tear down all active Firestore listeners and reset the store.
 * Call this on user logout.
 */
export function teardownRealtimeSubscriptions() {
  Object.entries(_unsubscribers).forEach(([name, unsub]) => {
    if (typeof unsub === 'function') {
      try {
        unsub();
      } catch (err) {
        console.warn(`[realtimeSubscriptions] Error unsubscribing ${name}:`, err);
      }
    }
  });

  _unsubscribers = {};
  _initialized = false;

  // Reset all store data
  useAppStore.getState().reset();
  console.info('[realtimeSubscriptions] All listeners torn down and store reset');
}
